import React, { useState } from 'react';
import { Form, Input, InputNumber, Select, Button, Upload, Row, Col, Card, Typography, Divider } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { storage, firestore } from 'config/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { addDoc, collection } from 'firebase/firestore';
import slugify from 'slugify';
import { useAuthContext } from 'contexts/AuthContext';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const initialState = {
    name: '',
    brand: '',
    price: 0,
    originalPrice: 0,
    shortDescription: '',
    description: '',
    features: [],
    colors: [],
    sizes: [],
    images: [],
};

const ProductUploadForm = () => {
    const { user } = useAuthContext();
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleUpload = async (values) => {
        setLoading(true);
        try {
            const uploadedImages = await uploadImages(fileList);

            const finalProductData = {
                ...values,
                images: uploadedImages,
                slug: slugify(values.name, { lower: true }),
                uploader: user.uid,
                uploadedAt: new Date(),
            };

            await addDoc(collection(firestore, 'products'), finalProductData);

            window.toastify('Product uploaded successfully!', "success");
            form.resetFields();
            setFileList([]);
        } catch (error) {
            console.error('Error uploading product:', error);
            window.toastify('Failed to upload product. Please try again.', "error");
        } finally {
            setLoading(false);
        }
    };

    const uploadImages = async (files) => {
        try {
            return await Promise.all(
                files.map(async (file) => {
                    const storageRef = ref(storage, `products/${window.getRandomId()}_${file.name}`);
                    const uploadTask = uploadBytesResumable(storageRef, file.originFileObj);
                    await uploadTask;
                    return await getDownloadURL(storageRef);
                })
            );
        } catch (error) {
            console.error('Error uploading images:', error);
            window.toastify('Failed to upload images. Please try again.', "error");
            throw error;
        }
    };

    return (
        <Card className="product-upload-card">
            <Title level={2}>Upload New Product</Title>
            <Form form={form} layout="vertical" onFinish={handleUpload} initialValues={initialState} >
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item name="name" label="Product Name" rules={[{ required: true, message: 'Please enter the product name' }]}>
                            <Input placeholder="e.g., Comfortable Cotton T-Shirt" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="brand" label="Brand" rules={[{ required: true, message: 'Please enter the brand name' }]}>
                            <Input placeholder="e.g., FashionBrand" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item name="price" label="Price" rules={[{ required: true, type: 'number', min: 0, message: 'Please enter a valid price' }]}>
                            <InputNumber formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="originalPrice" label="Original Price" rules={[{ required: true, type: 'number', min: 0, message: 'Please enter a valid original price' }]}>
                            <InputNumber formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="shortDescription" label="Short Description" rules={[{ required: true, message: 'Please enter a short description' }]}>
                    <TextArea rows={2} placeholder="Brief product description" />
                </Form.Item>

                <Form.Item name="description" label="Full Description" rules={[{ required: true, message: 'Please enter a full description' }]}>
                    <TextArea rows={4} placeholder="Detailed product description" />
                </Form.Item>

                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item name="colors" label="Colors" rules={[{ required: true, message: 'Please select at least one color' }]}>
                            <Select mode="multiple" placeholder="Select colors" style={{ width: '100%' }}>
                                <Option value="pink">Pink</Option>
                                <Option value="blue">Blue</Option>
                                <Option value="purple">Purple</Option>
                                <Option value="yellow">Yellow</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="sizes" label="Sizes" rules={[{ required: true, message: 'Please select at least one size' }]}>
                            <Select mode="multiple" placeholder="Select sizes" style={{ width: '100%' }}>
                                <Option value="s">S</Option>
                                <Option value="m">M</Option>
                                <Option value="l">L</Option>
                                <Option value="xl">XL</Option>
                                <Option value="xxl">XXL</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">Product Features</Divider>

                <Form.List name="features">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, index) => (
                                <Form.Item required={false} key={field.key}>
                                    <Form.Item {...field} validateTrigger={['onChange', 'onBlur']} rules={[{ required: true, whitespace: true, message: "Please input feature or delete this field.", },]} noStyle>
                                        <Input placeholder="Enter a product feature" style={{ width: 'calc(100% - 32px)' }} />
                                    </Form.Item>
                                    {fields.length > 1 && (
                                        <MinusCircleOutlined className="dynamic-delete-button" onClick={() => remove(field.name)} style={{ marginLeft: '8px' }} />
                                    )}
                                </Form.Item>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Feature
                                </Button>
                            </Form.Item>
                        </>
                    )}
                </Form.List>

                <Form.Item label="Product Images" required tooltip="Upload up to 4 images. The first image will be the main product image.">
                    <Upload listType="picture-card" fileList={fileList} onChange={({ fileList }) => setFileList(fileList)} beforeUpload={() => false} accept="image/*" multiple>
                        {fileList.length >= 4 ? null : (
                            <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                        )}
                    </Upload>
                    <Text type="secondary">You can upload a maximum of 4 images</Text>
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block size="large">
                        Upload Product
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default ProductUploadForm;